import {
  useSelector,
  type TypedUseSelectorHook,
} from 'react-redux';
import {
  createSelector
} from 'reselect';
import {
  UserStateEnum,
  CurrencyCodesEnum,
  UserRegistrationStatusEnum,
} from '@shared/types';
import {
  isDefined
} from '@shared/lib';
import {
  sessionSlice
} from './slice';
import {
  TagCodeEnum,
  TagStatusEnum,
  UserKycStatusEnum,
  CurrentVIPLevelEnum,
  VerificationStatusEnum,
  type Tag,
  type FullProfile,
  type UserBalance,
  type ShortProfile,
  type SessionState,
  type LoyaltyStatus,
  type CurrencySessionKeys,
} from './types';

type RootState = {
  [sessionSlice.name]: SessionState;
};

type CheckoutRequirements = {
  isAuthenticated: boolean
  kycRequired: boolean;
  allowedForNewPlayer: boolean;
  accessRestricted: boolean;
};

export const selectIsAuthenticated = (state: RootState): boolean => (
  isDefined(state[sessionSlice.name].shortProfile)
);

export const selectRegistrationStatus = (state: RootState): Maybe<UserRegistrationStatusEnum> => (
  state[sessionSlice.name].fullProfile?.registrationStatus
);

export const selectCurrencySessionKey = (state: RootState): Maybe<string> => {
  const currencySessionKeys = state[sessionSlice.name].currencySessionKeys;
  const toggledCurrencyCode = state[sessionSlice.name].toggledCurrencyCode;
  if (!currencySessionKeys) return null;
  return currencySessionKeys[toggledCurrencyCode]?.sessionKey;
};

export const selectCurrencySessionKeys = (state: RootState): Maybe<CurrencySessionKeys> => (
  state[sessionSlice.name].currencySessionKeys
);

export const selectFasttrackJwt = (state: RootState): string => (
  state[sessionSlice.name].fasttrackJwt
);

export const selectToggledCurrencyCode = (state: RootState): CurrencyCodesEnum => (
  state[sessionSlice.name].toggledCurrencyCode
);

export const selectUserStatus = (state: RootState): Maybe<UserStateEnum> => (
  state[sessionSlice.name]?.userState
);

export const selectShortProfile = (state: RootState): Maybe<ShortProfile> => (
  state[sessionSlice.name]?.shortProfile
);

export const selectHasPlayerRegistrationStatus = (state: RootState): boolean => (
  state[sessionSlice.name]?.fullProfile?.registrationStatus === UserRegistrationStatusEnum.Player
);

export const selectHasQuickRegistrationStatus = (state: RootState): boolean => (
  state[sessionSlice.name]?.fullProfile?.registrationStatus === UserRegistrationStatusEnum.QuickReg
);

export const selectKycStatus = (state: RootState): Maybe<UserKycStatusEnum> => (
  state[sessionSlice.name]?.fullProfile?.kycStatus
);

export const selectHasKycPassedStatus = (state: RootState): boolean => (
  state[sessionSlice.name]?.fullProfile?.kycStatus === UserKycStatusEnum.Pass
);

export const selectHasKycFailedStatus = (state: RootState): boolean => (
  state[sessionSlice.name]?.fullProfile?.kycStatus === UserKycStatusEnum.Failed
);

export const selectHasVerifiedStatus = (state: RootState): boolean => (
  state[sessionSlice.name]?.fullProfile?.verificationStatus === VerificationStatusEnum.Verified
);

export const selectFullProfile = (state: RootState): Maybe<FullProfile> => (
  state[sessionSlice.name]?.fullProfile
);

export const selectUserTags = createSelector(
  [(state: RootState) => state[sessionSlice.name]?.fullProfile?.tags],
  (tags) => tags
);

export const selectUserTagCodes = createSelector(
  [(state: RootState) => state[sessionSlice.name]?.fullProfile?.tags],
  (tags) => tags?.map(({ code }) => code)
);

export const selectSessionKey = (state: RootState): string => (
  state[sessionSlice.name].sessionKey
);

export const selectUserBalance = (state: RootState): Maybe<UserBalance> => (
  state[sessionSlice.name].userBalance
);

export const selectEmailPromotionSubscription = (state: RootState): boolean => (
  state[sessionSlice.name]?.emailPromotionSubscription
);

export const selectCurrentVIPLevel = (state: RootState): Maybe<CurrentVIPLevelEnum> => (
  state[sessionSlice.name]?.loyaltyStatus?.currentVIPLevel
);

export const selectLoyaltyStatus = (state: RootState): Maybe<LoyaltyStatus> => (
  state[sessionSlice.name]?.loyaltyStatus
);

export const selectActiveTags = (state: RootState): Maybe<Array<Tag>> => (
  state[sessionSlice.name]?.fullProfile?.tags?.filter(tag => tag.status === TagStatusEnum.Active)
);

export const selectHasIdVerifiedTag = (state: RootState): boolean => (
  state[sessionSlice.name]?.fullProfile?.tags?.some(tag => tag.code === TagCodeEnum.IdVerified) ?? false
);

export const selectHasSCForbiddenTag = (state: RootState): boolean => (
  state[sessionSlice.name]?.fullProfile?.tags?.some(tag => tag.code === TagCodeEnum.IsSCForbidden) ?? false
);

export const selectHasFirstTimeDepositTag = (state: RootState): boolean => (
  state[sessionSlice.name]?.fullProfile?.tags?.some(tag => tag.code === TagCodeEnum.FtdYes) ?? false
);

export const selectHasFifthTimesDepositTag = (state: RootState): boolean => (
  state[sessionSlice.name]?.fullProfile?.tags?.some(tag => tag.code === TagCodeEnum.FifthDepositYes) ?? false
);

export const selectCheckoutRequirements = createSelector(
  [selectIsAuthenticated, selectRegistrationStatus, selectKycStatus, selectHasFifthTimesDepositTag, selectHasIdVerifiedTag, selectHasSCForbiddenTag],
  (isAuthenticated, registrationStatus, kycStatus, hasFifthTimesDepositTag, hasIdVerifiedTag, hasSCForbiddenTag): CheckoutRequirements => {
    const isQuickReg = registrationStatus === UserRegistrationStatusEnum.QuickReg;
    const isPlayer = registrationStatus === UserRegistrationStatusEnum.Player;

    const isKycPass = kycStatus === UserKycStatusEnum.Pass;
    const isKycOpen = kycStatus === UserKycStatusEnum.Open;
    const isKycPending = kycStatus === UserKycStatusEnum.Pending;
    const isKycRequested = kycStatus === UserKycStatusEnum.Requested;
    const isKycFailed = kycStatus === UserKycStatusEnum.Failed;
    const isKycUnknown = kycStatus === UserKycStatusEnum.Unknown;

    let defaultBuyCoinsRequirements: CheckoutRequirements = {
      isAuthenticated,
      allowedForNewPlayer: false,
      kycRequired: false,
      accessRestricted: false
    };

    if (!isAuthenticated) {
      return { ...defaultBuyCoinsRequirements, isAuthenticated: false };
    };

    const isAccessRestricted =
      isQuickReg ||
      // TODO: keep for now, don't touch this for now, it will be removed in the future
      (isQuickReg && isKycUnknown) ||
      (isPlayer && isKycFailed) ||
      (isPlayer && (isKycPending || isKycRequested) && hasFifthTimesDepositTag && hasIdVerifiedTag);
    if (isAccessRestricted) {
      defaultBuyCoinsRequirements = { ...defaultBuyCoinsRequirements, accessRestricted: true };
    }

    const isKycRequired =
      (isPlayer && (isKycUnknown || isKycOpen) && hasFifthTimesDepositTag) ||
      (isPlayer && (isKycPending || isKycRequested) && hasFifthTimesDepositTag);
    if (isKycRequired) {
      defaultBuyCoinsRequirements = { ...defaultBuyCoinsRequirements, kycRequired: true };
    }

    const allowedForNewPlayer = !hasSCForbiddenTag &&
      (isQuickReg && (isKycUnknown || isKycOpen || isKycPending || isKycRequested) && !hasFifthTimesDepositTag) ||
      (isPlayer && isKycPass) ||
      (isPlayer && (isKycUnknown || isKycOpen)) ||
      (isPlayer && (isKycPending || isKycRequested));
    if (allowedForNewPlayer) {
      defaultBuyCoinsRequirements = { ...defaultBuyCoinsRequirements, allowedForNewPlayer: true };
    }

    return defaultBuyCoinsRequirements;
  });

export const selectAdditionalDocumentsRequired = createSelector(
  [selectRegistrationStatus, selectKycStatus, selectHasFifthTimesDepositTag, selectHasIdVerifiedTag],
  (registrationStatus, kycStatus, hasFifthTimesDepositTag, hasIdVerifiedTag): boolean => {
    const isPlayer = registrationStatus === UserRegistrationStatusEnum.Player;
    const isKycPending = kycStatus === UserKycStatusEnum.Pending;
    const isKycRequested = kycStatus === UserKycStatusEnum.Requested;

    return (isPlayer && (isKycPending || isKycRequested) && hasFifthTimesDepositTag && hasIdVerifiedTag);
  });

export const selectIsPlayerVerified = (state: RootState): boolean => {
  const fullProfile = state[sessionSlice.name]?.fullProfile;
  return fullProfile?.kycStatus === UserKycStatusEnum.Pass &&
    fullProfile?.registrationStatus === UserRegistrationStatusEnum.Player;
};

export const selectWithdrawCoinsAccessRequirements = createSelector([
  selectKycStatus,
  selectIsAuthenticated,
  selectRegistrationStatus,
], (kycStatus, isAuthenticated, registrationStatus): boolean => {
  return (
    registrationStatus === UserRegistrationStatusEnum.Player &&
    kycStatus === UserKycStatusEnum.Pass &&
    isAuthenticated
  );
});

export const useSessionSelector: TypedUseSelectorHook<RootState> = useSelector;